import { useEffect, useMemo } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchCurrencySelectOptions } from '#/api/system/currency';
import { fetchPaymentTermSelectOptions } from '#/api/system/paymentTerm';
import {
  createMiscPurchaseOrder,
  fetchMiscPurchaseOrderDetail,
  updateMiscPurchaseOrder,
} from '#/api/srm/miscPurchaseOrder';
import { ROUTES } from '#/config/routes';
import { useFormValidationSummary } from '#/hooks/useFormValidationSummary';
import { Button } from '#/modules/commonComponents/button';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import FloatingSelect from '#/modules/commonComponents/form/select';
import PageShell from '#/modules/infra/layout/PageShell';
import { applyDocumentCurrencyFieldError } from '#/utils/documentCurrencyError';
import {
  lookupQueryKey,
  miscPurchaseOrderDetailQueryKey,
  miscPurchaseOrderListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

import MiscPurchaseOrderLinesEditor from '../components/MiscPurchaseOrderLinesEditor';
import MiscPurchaseOrderStatusBadge from '../components/MiscPurchaseOrderStatusBadge';
import {
  getMiscPurchaseOrderApiErrorMessage,
  isMiscPurchaseOrderVersionConflict,
} from '../helper/apiError';
import { canEditMiscPurchaseOrderDraft } from '../helper/miscPurchaseOrderStatus';
import {
  DEFAULT_MISC_PURCHASE_ORDER_FORM,
  MISC_PURCHASE_ORDER_FIELD_LABELS,
  MISC_PURCHASE_ORDER_ROUNDING_MODE_OPTIONS,
  MISC_PURCHASE_ORDER_TAX_TYPE_OPTIONS,
  mapMiscPurchaseOrderDetailToForm,
  miscPurchaseOrderFormSchema,
  pickMiscPurchaseOrderUpdatedAt,
} from '../schemas/miscPurchaseOrderSchema';

const CreateEditMiscPurchaseOrderPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: miscPurchaseOrderDetailQueryKey(id),
    queryFn: () => fetchMiscPurchaseOrderDetail(id),
    enabled: isEdit,
  });
  const detail = detailQuery.data?.data ?? null;

  const currencyQuery = useQuery({
    queryKey: lookupQueryKey('currencySelect'),
    queryFn: fetchCurrencySelectOptions,
  });
  const paymentTermQuery = useQuery({
    queryKey: lookupQueryKey('paymentTermSelect'),
    queryFn: fetchPaymentTermSelectOptions,
  });

  const currencyOptions = useMemo(
    () => currencyQuery.data ?? [],
    [currencyQuery.data],
  );
  const paymentTermOptions = useMemo(
    () => paymentTermQuery.data ?? [],
    [paymentTermQuery.data],
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(miscPurchaseOrderFormSchema),
    defaultValues: DEFAULT_MISC_PURCHASE_ORDER_FORM,
  });

  useEffect(() => {
    if (!isEdit || !detail) {
      return;
    }
    reset(mapMiscPurchaseOrderDetailToForm(detail));
  }, [detail, isEdit, reset]);

  const saveMutation = useMutation({
    mutationFn: (values) => {
      if (!isEdit) {
        return createMiscPurchaseOrder(values);
      }
      return updateMiscPurchaseOrder(id, {
        ...values,
        updatedAt: pickMiscPurchaseOrderUpdatedAt(detail),
      });
    },
    onSuccess: async (result) => {
      toast.success(result?.message || (isEdit ? '非庫存採購單已更新' : '非庫存採購單已建立'));
      resetValidationSummary();
      await queryClient.invalidateQueries({
        queryKey: miscPurchaseOrderListQueryKeyPrefix(),
      });
      const nextId = result?.data?.id || id;
      if (!isEdit && nextId) {
        navigate(ROUTES.miscPurchaseOrderDetail(nextId));
        return;
      }
      await queryClient.invalidateQueries({
        queryKey: miscPurchaseOrderDetailQueryKey(id),
      });
    },
    onError: (error) => {
      toast.error(getMiscPurchaseOrderApiErrorMessage(
        error,
        isEdit ? '更新非庫存採購單失敗' : '建立非庫存採購單失敗',
      ));
      applyDocumentCurrencyFieldError(error, setError);
      if (isEdit && isMiscPurchaseOrderVersionConflict(error)) {
        detailQuery.refetch();
      }
    },
  });

  function onSubmit(values) {
    markSubmitAttempted();
    resetValidationSummary();
    saveMutation.mutate(values);
  }

  if (isEdit && detail && !canEditMiscPurchaseOrderDraft(detail.status)) {
    return <Navigate to={ROUTES.miscPurchaseOrderDetail(id)} replace />;
  }

  const pending = saveMutation.isPending;
  const pageTitle = isEdit ? '編輯非庫存採購單' : '新建非庫存採購單';

  return (
    <PageShell variant="formTall">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-6xl">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0! flex items-center gap-2">
              <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
              {isEdit && detail?.status ? (
                <MiscPurchaseOrderStatusBadge status={detail.status} size="sm" />
              ) : null}
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={isEdit ? ROUTES.miscPurchaseOrderDetail(id) : ROUTES.miscPurchaseOrder}>
                {isEdit ? '返回詳情' : '返回列表'}
              </Link>
            </Button>
          </div>

          {isEdit && detailQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : null}
          {isEdit && detailQuery.isError ? (
            <p className="text-sm text-destructive">
              {getMiscPurchaseOrderApiErrorMessage(detailQuery.error, '載入非庫存採購單失敗')}
            </p>
          ) : null}

          {!isEdit || detail ? (
            <form
              className="sheet"
              onSubmit={handleSubmit(onSubmit, markSubmitAttempted)}
              noValidate
            >
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={MISC_PURCHASE_ORDER_FIELD_LABELS}
                className="mb-4"
              />
              <div className="create_edit_form__body divide-y divide-gray-500">
                <section className="pb-6">
                  <div className="form-block">
                    <h2 className="form-block__title">單據資訊</h2>
                  </div>
                  <div className="grid grid-cols-1 gap-3 @2xl/sheet:grid-cols-2">
                    <FloatingDateInput
                      id="purchaseOrderDate"
                      label="採購日"
                      control={control}
                      errors={errors}
                      disabled={pending}
                    />
                    <FloatingInput
                      id="supplierName"
                      label="供應商"
                      register={register}
                      errors={errors}
                      disabled={pending}
                    />
                    <FloatingSelect
                      id="currencyId"
                      label="幣別"
                      options={currencyOptions}
                      control={control}
                      errors={errors}
                      isDisabled={pending || currencyQuery.isLoading}
                    />
                    <FloatingSelect
                      id="paymentTermId"
                      label="付款條件"
                      options={paymentTermOptions}
                      control={control}
                      errors={errors}
                      isClearable
                      isDisabled={pending || paymentTermQuery.isLoading}
                    />
                    <FloatingSelect
                      id="taxType"
                      label="稅別"
                      options={MISC_PURCHASE_ORDER_TAX_TYPE_OPTIONS}
                      control={control}
                      errors={errors}
                      isDisabled={pending}
                    />
                    <FloatingSelect
                      id="roundingMode"
                      label="進位"
                      options={MISC_PURCHASE_ORDER_ROUNDING_MODE_OPTIONS}
                      control={control}
                      errors={errors}
                      isDisabled={pending}
                    />
                    <FloatingTextarea
                      id="issueReason"
                      label="採購事由"
                      register={register}
                      errors={errors}
                      rows={2}
                      disabled={pending}
                    />
                    <FloatingTextarea
                      id="remark"
                      label="備註"
                      register={register}
                      errors={errors}
                      rows={2}
                      disabled={pending}
                    />
                  </div>
                </section>
                <MiscPurchaseOrderLinesEditor
                  control={control}
                  register={register}
                  errors={errors}
                  disabled={pending}
                />
              </div>
              <div className="create_edit_form__actions mt-4 flex justify-end">
                <Button type="submit" disabled={pending}>
                  {pending ? '儲存中…' : '儲存'}
                </Button>
              </div>
            </form>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditMiscPurchaseOrderPage;
